/* FanFuel — all marketing copy in one place (MVP). */

export const BRAND = {
  master: "Turn your audience into income — without giving up control.",
  supporting:
    "Launch support, AI-powered growth, clear cashouts and real human oversight for Caribbean creators.",
  promise: "No upfront fees. No monthly fees. FanFuel earns when creators earn.",
  trust: "Your account. Your content. Your decisions.",
  pillars: ["Launch", "Grow", "Cash out", "Control"],
};

export const NAV = [
  { label: "For Creators", to: "/creators" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "Cashouts", to: "/cashouts" },
  { label: "Privacy & Trust", to: "/privacy-trust" },
  { label: "Ambassadors", to: "/ambassadors" },
  { label: "FAQ", to: "/faq" },
];

export const TRUST_STRIP = [
  "No upfront fees",
  "No monthly fees",
  "No passwords",
  "Human approval for money decisions",
];

export const PILLAR_CARDS = [
  {
    key: "launch",
    tag: "01",
    title: "Launch",
    body: "Set up your creator business with profile positioning, pricing guidance, launch planning, and support.",
    accent: "ember",
  },
  {
    key: "grow",
    tag: "02",
    title: "Grow",
    body: "Use AI-assisted captions, campaign ideas, content calendars, and coaching to stay consistent.",
    accent: "teal",
  },
  {
    key: "cashout",
    tag: "03",
    title: "Cash out",
    body: "Submit cashout requests, upload proof, track status, and receive clear payout updates.",
    accent: "amber",
  },
  {
    key: "control",
    tag: "04",
    title: "Control",
    body: "You keep your account, content, identity, boundaries, and final decisions.",
    accent: "ivory",
  },
];

export const MANIFESTO = [
  {
    n: "01",
    title: "Creators keep control",
    body: "Your account, your content, your identity, your boundaries. FanFuel is infrastructure around you — never a replacement for your judgment.",
  },
  {
    n: "02",
    title: "AI drafts. You decide.",
    body: "AI helps you move faster with captions, campaigns and calendars. Every AI output is a draft. You choose what actually gets posted.",
  },
  {
    n: "03",
    title: "Humans guard the money",
    body: "AI may summarize, parse, flag and calculate. It does not approve, deny, mark paid, or move funds. A human reviews every money decision.",
  },
  {
    n: "04",
    title: "Aligned incentives",
    body: "No upfront fees. No monthly fees. FanFuel earns when creators earn — so growth only matters if it's yours first.",
  },
];

export const JOURNEY = [
  { step: "01", title: "Apply", body: "Tell us about you and your audience. It takes a few minutes." },
  { step: "02", title: "Qualify", body: "We review your fit and reach out via Telegram or email." },
  { step: "03", title: "Launch or improve", body: "Set up or refine your paid platform with positioning and pricing guidance." },
  { step: "04", title: "AI content & coaching", body: "Get AI-assisted captions, campaign ideas, calendars and consistent coaching." },
  { step: "05", title: "Request cashouts", body: "Submit a cashout request whenever you're ready." },
  { step: "06", title: "Upload proof", body: "Attach the required proof so the request can be reviewed." },
  { step: "07", title: "Human review", body: "A real person reviews the request and makes the decision." },
  { step: "08", title: "Status & payout reference", body: "Approved requests receive a reference and a clear status update." },
];

export const CASHOUT_STEPS = [
  { step: "01", title: "Submit your cashout request", body: "Start a request from your creator workspace whenever you're ready." },
  { step: "02", title: "Upload required proof", body: "Attach the documentation needed so the request can be verified." },
  { step: "03", title: "FanFuel prepares the review", body: "We check the information and organize everything for a clean review." },
  { step: "04", title: "A human reviews & decides", body: "A real person reviews the request and makes the approval decision." },
  { step: "05", title: "Reference & status update", body: "If approved, the cashout receives a reference and transparent status update." },
];

export const MONEY_MESSAGE =
  "AI may summarize, parse, flag, and calculate. AI does not approve, deny, mark paid, or send funds without explicit human approval.";

export const PAYOUT_METHODS = [
  { name: "Western Union", note: "Planned / supported where available" },
  { name: "RIA", note: "Planned / supported where available" },
  { name: "MoneyGram", note: "Planned / supported where available" },
  { name: "PayPal", note: "Planned / supported where available" },
  { name: "Bank transfer", note: "Coming later" },
];

export const PRIVACY_POINTS = [
  { title: "We never ask for your platform password", body: "FanFuel does not request or store OnlyFans, Fansly or any platform passwords. Ever.", tone: "positive" },
  { title: "Your account remains yours", body: "You keep ownership and access to your own creator accounts at all times.", tone: "positive" },
  { title: "Your content remains yours", body: "You own what you create. FanFuel does not take ownership of your content.", tone: "positive" },
  { title: "Your decisions remain yours", body: "You have the final say on positioning, posting, pricing and boundaries.", tone: "positive" },
  { title: "AI creates drafts, not final posts", body: "AI output is a starting point. Nothing goes live without your choice.", tone: "neutral" },
  { title: "Human approval protects money decisions", body: "Every payout decision passes through a real human reviewer.", tone: "neutral" },
  { title: "Private files and proof handled securely", body: "Sensitive documents and proof are handled with care and access controls.", tone: "neutral" },
  { title: "Sensitive actions require approval", body: "Money movement and high-impact actions are gated behind explicit approval.", tone: "warning" },
  { title: "No earnings guarantees", body: "We do not promise guaranteed earnings or guaranteed payouts. Results depend on you and your audience.", tone: "warning" },
];

export const AMBASSADOR_TIERS = [
  {
    name: "Community",
    level: "Tier 01",
    body: "Start referring creators with your personal code. Track every verified referral transparently.",
    features: ["Personal referral code & link", "Transparent referral tracking", "Community support"],
    accent: "grey",
  },
  {
    name: "Core",
    level: "Tier 02",
    body: "For active ambassadors bringing consistent, qualified creators into the network.",
    features: ["Everything in Community", "Priority referral review", "Enhanced tracking dashboard (planned)"],
    accent: "teal",
    featured: true,
  },
  {
    name: "Lead / Regional",
    level: "Tier 03",
    body: "For ambassadors building regional reach across the Caribbean creator ecosystem.",
    features: ["Everything in Core", "Regional recognition", "Direct line to the FanFuel team"],
    accent: "ember",
  },
];

export const FAQ = [
  {
    q: "What is FanFuel?",
    a: "FanFuel is an AI-operated creator growth and monetization platform for Caribbean creators. We help you launch a paid creator business, grow with AI-assisted coaching, and manage cashouts through a clear, approval-controlled process — while you keep control of your account, content and decisions.",
  },
  {
    q: "Who is FanFuel for?",
    a: "Caribbean creators with an existing audience, creators active on X/Twitter or other social platforms, creators who want to launch paid platforms, and existing subscription creators who need content, growth, privacy and cashout support.",
  },
  {
    q: "Does FanFuel charge upfront fees?",
    a: "No. There are no upfront fees and no monthly fees. FanFuel earns when creators earn.",
  },
  {
    q: "Does FanFuel need my OnlyFans/Fansly password?",
    a: "No. FanFuel never asks for your platform passwords and never stores them. Your account stays yours.",
  },
  {
    q: "Does AI post for me?",
    a: "No. AI creates drafts — captions, campaign ideas, content calendars and coaching prompts. You always choose what actually gets posted.",
  },
  {
    q: "How do cashouts work?",
    a: "You submit a cashout request and upload the required proof. FanFuel prepares the review, a human reviews and decides, and approved payments receive a reference and status update. AI never approves, denies, marks paid or moves funds on its own.",
  },
  {
    q: "Are payouts guaranteed?",
    a: "No. We do not guarantee earnings or payouts. Payout decisions are reviewed by a human, and results depend on you and your audience.",
  },
  {
    q: "What countries do you support?",
    a: "FanFuel focuses on Caribbean creators. Supported payout methods are rolled out where available — tell us your country in your application and we'll confirm.",
  },
  {
    q: "Can I apply if I am not earning yet?",
    a: "Yes. If you have an audience and want to launch a paid platform, you can apply. Launch support is part of what FanFuel helps with.",
  },
  {
    q: "How does the Ambassador Program work?",
    a: "Ambassadors refer creators using a personal code or link and can earn tracked rewards when eligibility conditions are met. There's no promise of commission until a referral is verified and eligible.",
  },
  {
    q: "What happens after I apply?",
    a: "We review your application for fit and reach out via Telegram or email. From there we help you launch or improve your paid platform and get you set up with content and coaching.",
  },
];

export const CREATOR_HELPS = [
  "Launch positioning, pricing guidance and a launch plan",
  "AI-assisted captions, campaign ideas and content calendars",
  "Consistent coaching to help you stay on track",
  "A clear, approval-controlled cashout process",
  "Privacy safeguards and handling of sensitive proof",
];

export const CREATOR_DOESNT = [
  "We do not ask for your platform passwords",
  "We do not take ownership of your account or content",
  "We do not post on your behalf without your decision",
  "We do not move money automatically — humans approve payouts",
  "We do not guarantee earnings or payouts",
];

export const OWNERSHIP_RULES = [
  { title: "Your account", body: "You keep ownership and access to your creator accounts." },
  { title: "Your content", body: "Everything you create belongs to you." },
  { title: "Your identity", body: "You control how you're presented and what you share." },
  { title: "Your decisions", body: "Final calls on posting, pricing and boundaries are yours." },
];

export const CREATOR_AUDIENCE = [
  "Caribbean creators with an existing audience",
  "Creators active on X/Twitter or other social platforms",
  "Creators who want to launch paid platforms",
  "Existing subscription creators who need content, growth, privacy & cashout support",
];

export const CREATOR_TRUST_PROOF = [
  {
    icon: "lock",
    title: "No platform passwords",
    body: "FanFuel is designed so creators keep control of their own accounts.",
    tone: "positive",
  },
  {
    icon: "pen",
    title: "AI drafts, creator decisions",
    body: "FanFuel helps draft captions, launch plans, and growth ideas — creators choose what gets posted.",
    tone: "neutral",
  },
  {
    icon: "shield",
    title: "Human approval for money decisions",
    body: "Cashout requests use proof, review stages, and human approval before payment status changes.",
    tone: "ember",
  },
  {
    icon: "globe",
    title: "Caribbean cashout support",
    body: "FanFuel is built around the real payout friction Caribbean creators face.",
    tone: "positive",
  },
];

export const IMAGES = {
  heroCreator:
    "https://images.unsplash.com/photo-1783012039560-5690190a9b9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxjYXJpYmJlYW4lMjBjcmVhdG9yJTIwcG9ydHJhaXQlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODQwNDkzMDF8MA&ixlib=rb-4.1.0&q=85",
  portrait:
    "https://images.unsplash.com/photo-1762291629616-3e2c044c79a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxjYXJpYmJlYW4lMjBjcmVhdG9yJTIwcG9ydHJhaXQlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODQwNDkzMDF8MA&ixlib=rb-4.1.0&q=85",
  studio:
    "https://images.unsplash.com/photo-1478737270239-2f02b77fc618?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxibGFjayUyMHBvZGNhc3RlciUyMHN0dWRpbyUyMG1pY3JvcGhvbmUlMjBkYXJrfGVufDB8fHx8MTc4NDA0OTMwMnww&ixlib=rb-4.1.0&q=85",
  dataLines:
    "https://images.unsplash.com/photo-1750969185331-e03829f72c7d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdsb3dpbmclMjBkYXRhJTIwbGluZXMlMjBuZXR3b3JrJTIwZGFya3xlbnwwfHx8fDE3ODQwNDkyODZ8MA&ixlib=rb-4.1.0&q=85",
};
